import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesService, CreateInvoiceDto } from '@/features/invoices/services/invoices.service';
import { InvoiceSubmitModal } from '@/features/invoices/components/InvoiceSubmitModal';
import { InvoiceEditModal } from '@/features/invoices/components/InvoiceEditModal';
import { ContractCard } from '@/features/invoices/components/ContractCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, FileText, Download, Plus, Edit } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/invoices')({
  component: InvoicesPage,
});

function formatMonth(monthStr: string) {
  const [year, month] = monthStr.split('-');
  const date = new Date(Number.parseInt(year), Number.parseInt(month) - 1);
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

function formatCurrency(value: string | number) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function InvoicesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<{ id: string; contractId?: string | null } | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['my-invoices'],
    queryFn: invoicesService.getMyInvoices,
  });

  const { data: pendingData } = useQuery({
    queryKey: ['pending-month'],
    queryFn: invoicesService.getPendingMonth,
  });

  const { data: contracts = [], isLoading: isContractsLoading } = useQuery({
    queryKey: ['my-contracts'],
    queryFn: invoicesService.getMyContracts,
  });

  const submitMutation = useMutation({
    mutationFn: ({ data, file }: { data: CreateInvoiceDto; file: File }) =>
      invoicesService.submitInvoice(data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['pending-month'] });
      toast({
        title: 'Nota enviada',
        description: 'Sua nota fiscal foi enviada com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Não foi possível enviar a nota fiscal.',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = async (data: CreateInvoiceDto, file: File) => {
    await submitMutation.mutateAsync({ data, file });
  };

  const updateInvoiceMutation = useMutation({
    mutationFn: ({ invoiceId, contractId }: { invoiceId: string; contractId: string | null }) =>
      invoicesService.updateInvoice(invoiceId, { contractId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-invoices'] });
      toast({
        title: 'Nota atualizada',
        description: 'O contrato da nota fiscal foi atualizado com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Não foi possível atualizar a nota fiscal.',
        variant: 'destructive',
      });
    },
  });

  const handleEdit = (invoice: { id: string; contractId?: string | null }) => {
    setSelectedInvoice(invoice);
    setIsEditModalOpen(true);
  };

  const handleUpdateContract = async (contractId: string | null) => {
    if (!selectedInvoice) return;
    await updateInvoiceMutation.mutateAsync({
      invoiceId: selectedInvoice.id,
      contractId,
    });
  };

  const handleDownload = async (invoiceId: string) => {
    try {
      await invoicesService.downloadInvoice(invoiceId);
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível baixar o arquivo.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading || isContractsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[100px] w-full" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  const hasContracts = contracts.length > 0;
  const today = new Date().toISOString().slice(0, 10);
  const hasActiveContracts = contracts.some(
    (contract) => contract.startDate <= today && contract.endDate >= today,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Notas Fiscais</h1>
          <p className="text-muted-foreground">
            Gerencie suas notas fiscais de prestação de serviço
          </p>
        </div>
        {hasActiveContracts && (
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Nota
          </Button>
        )}
      </div>

      {!hasContracts && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Faltam informações</AlertTitle>
          <AlertDescription>
            Faltam informações, contate a equipe de gestão.
          </AlertDescription>
        </Alert>
      )}

      {hasActiveContracts && pendingData?.pendingMonth && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Nota Pendente</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              Você ainda não emitiu a nota fiscal referente a{' '}
              <strong>{formatMonth(pendingData.pendingMonth)}</strong>.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-4"
              onClick={() => setIsModalOpen(true)}
            >
              Emitir Agora
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {hasContracts && <ContractCard contracts={contracts} />}

      {hasActiveContracts && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Histórico de Notas
            </CardTitle>
            <CardDescription>Todas as notas fiscais enviadas</CardDescription>
          </CardHeader>
          <CardContent>
            {invoices && invoices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mês de Referência</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data de Envio</TableHead>
                    <TableHead>Data de Pagamento</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => {
                    const hasNoContract = !invoice.contractId;
                    return (
                      <TableRow
                        key={invoice.id}
                        className={hasNoContract ? 'border-l-4 border-l-yellow-500' : ''}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {formatMonth(invoice.referenceMonth)}
                            {hasNoContract && (
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                                Sem contrato
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{formatCurrency(invoice.value)}</TableCell>
                        <TableCell>
                          <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                            {invoice.status === 'paid' ? 'Paga' : 'Pendente'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(invoice.submittedAt).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          {invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString('pt-BR') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(invoice)}
                              title="Editar contrato"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDownload(invoice.id)}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="mx-auto h-12 w-12 mb-4 opacity-50" />
                <p>Nenhuma nota fiscal enviada ainda.</p>
                <Button className="mt-4" onClick={() => setIsModalOpen(true)}>
                  Enviar Primeira Nota
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {hasActiveContracts && (
        <>
          <InvoiceSubmitModal
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            onSubmit={handleSubmit}
            suggestedMonth={pendingData?.pendingMonth}
            contracts={contracts}
          />
          <InvoiceEditModal
            open={isEditModalOpen}
            onOpenChange={setIsEditModalOpen}
            onSubmit={handleUpdateContract}
            contracts={contracts}
            currentContractId={selectedInvoice?.contractId}
          />
        </>
      )}
    </div>
  );
}
